import {
  DueDisplayOptions,
  GroupByMode,
  GroupSetting,
  newTaskSuffix,
  overdueSectionID,
} from '@griply/types';
import {
  getNewTaskPrefill,
  getTaskIdFromTreeId,
  parseDateKey,
} from '@griply/utils';
import React, { useMemo } from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  TaskListItemTypes,
} from 'shared/components/ui/virtualized-tree-list';

import { DeadlineHeader } from '../deadline-header';
import { TreeTaskCard } from '../tree-task-card';
import { useMultipleDeadlinesRenderData } from './multiple-deadlines-render-context';

const EMPTY_DISPLAY_DUE: DueDisplayOptions[] = [];

export const MultipleDeadlinesRenderItem: React.FC<RenderItemProps> = ({
  item,
  itemInstance,
  tree,
  isMultiSelectModifierPressed,
}) => {
  const {
    tasksMap,
    tasks,
    today,
    denseFormat,
    defaultNewTaskDate,
    goalId,
    lifeAreaId,
    tagId,
    displayDue,
    showRepeatingAsTodo,
    completionData,
    overdueTasks,
  } = useMultipleDeadlinesRenderData();

  const parentId = itemInstance.getParent()?.getItemData().sourceId;

  const sectionDate = useMemo(() => {
    if (!parentId) {
      return undefined;
    }

    const date = parseDateKey(parentId);
    return isNaN(date.getTime()) ? undefined : date;
  }, [parentId]);

  const type = item.type;

  if (type === TaskListItemTypes.Task) {
    const effectiveDisplayDue =
      parentId === overdueSectionID ? displayDue : EMPTY_DISPLAY_DUE;

    const taskId = getTaskIdFromTreeId(item.sourceId);
    const task = tasksMap[taskId];
    if (!task) {
      return null;
    }

    return (
      <TreeTaskCard
        task={task}
        tree={tree}
        item={itemInstance}
        tasks={tasks}
        isMultiSelectModifierPressed={isMultiSelectModifierPressed}
        completionDate={sectionDate}
        displayDate={sectionDate}
        displayDue={effectiveDisplayDue}
        showRepeatingAsTodo={showRepeatingAsTodo}
        precomputedCompletion={completionData?.[item.sourceId]}
      />
    );
  }

  if (type === TaskListItemTypes.NewTask) {
    const groupSectionId = item.sourceId.replace(newTaskSuffix, '');
    const groupSetting: GroupSetting = { goalId, lifeAreaId, tagId };
    const prefill = getNewTaskPrefill({
      groupByMode: GroupByMode.Deadline,
      groupSectionId,
      groupSetting,
      today,
      defaultNewTaskDate,
    });
    return (
      <NewTaskRow
        date={prefill.startDate}
        dateAnchored={prefill.dateAnchored}
        startTime={null}
        duration={null}
        goal={prefill.goalId}
        lifeArea={prefill.lifeAreaId}
        tags={prefill.tagIds}
      />
    );
  }

  if (type === TaskListItemTypes.Date) {
    return (
      <DeadlineHeader
        denseFormat={denseFormat}
        item={itemInstance}
        overdueTasks={overdueTasks}
      />
    );
  }

  return null;
};
